import { useState, useEffect } from 'react';
import {
  TrendingUp, Target, Building2, Users, CheckCircle2,
  Clock, Rocket, MonitorSmartphone, ShieldAlert,
  Server, Flag, AlertTriangle, Play, ChevronRight, Zap,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { LoadingSpinner, ErrorPanel } from '../../components/dashboard/DashboardShared';
import { ROUTES } from '../../api/routes';

/* ─── Components ─── */

function MetaCard({ label, meta, atual, unidade }: { label: string; meta: number; atual: number; unidade: string }) {
  const pct = Math.round((atual / meta) * 100);
  const color = pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';
  const formatted = (v: number) => unidade === 'R$' ? `R$ ${v.toLocaleString('pt-BR')}` : v.toLocaleString('pt-BR');
  return (
    <div style={{
      background: 'var(--bg-card, white)', borderRadius: 16,
      border: '1px solid var(--border-color, #e5e7eb)', padding: '18px 22px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `${color}30` }}>
        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, transition: 'width .6s ease' }} />
      </div>
      <div style={{ fontSize: '.72rem', color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-main, #111)' }}>{formatted(atual)}</span>
        <span style={{ fontSize: '.8rem', color: 'var(--text-muted, #9ca3af)' }}>/ {formatted(meta)}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 4, background: 'var(--border-color, #e5e7eb)' }}>
          <div style={{ height: '100%', borderRadius: 4, width: `${Math.min(pct, 100)}%`, background: color, transition: 'width .6s ease' }} />
        </div>
        <span style={{ fontSize: '.75rem', fontWeight: 800, color }}>{pct}%</span>
      </div>
    </div>
  );
}

function PendenciaRow({ deal }: { deal: any }) {
  const probColor = deal.score >= 80 ? '#10b981' : deal.score >= 50 ? '#f59e0b' : deal.score >= 30 ? '#ef4444' : '#9ca3af';
  const faseIcons: Record<string, any> = {
    'Setup Acadêmico (Alunos)': Users,
    'Setup Infra (Equipamentos)': MonitorSmartphone,
    'Ativação de Hardware': Server,
    'Pre-Live (Aguardando Fluxos)': Clock,
  };
  const FaseIcon = faseIcons[deal.fase] || Target;
  
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
      background: 'var(--bg-main, #f9fafb)', borderRadius: 12,
      borderLeft: `4px solid ${probColor}`, transition: 'all .15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(3px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${probColor}12`, color: probColor, flexShrink: 0,
      }}>
        <FaseIcon size={18} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '.88rem' }}>{deal.escola}</div>
        <div style={{ fontSize: '.72rem', color: 'var(--text-muted, #6b7280)', marginTop: 2, display: 'flex', gap: 8 }}>
          <span>{deal.cidade}</span>
          <span>·</span>
          <span>{deal.alunos.toLocaleString('pt-BR')} alunos</span>
          <span>·</span>
          <span style={{ color: deal.diasAberto > 15 ? '#ef4444' : 'inherit' }}>{deal.diasAberto}d em aberto</span>
        </div>
        <div style={{ fontSize: '.76rem', color: '#dc2626', fontWeight: 600, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <AlertTriangle size={12} /> {deal.impedimento}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '4px 8px', borderRadius: 6, fontSize: '.68rem', fontWeight: 700,
          background: `${probColor}12`, color: probColor,
        }}>
          {deal.fase}
        </span>
      </div>
    </div>
  );
}

/* ─── Main ─── */
export function MasterCrescimentoPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    import('../../api/client').then(({ api }) => {
      api.get(ROUTES.master.crescimento)
        .then(r => {
          setData(r.data);
          setLoading(false);
        })
        .catch(() => {
          setError(true);
          setLoading(false);
        });
    });
  }, []);

  if (loading) return <LoadingSpinner label="Carregando Operação..." />;
  if (error) return <ErrorPanel />;

  const d = data || {};
  const historico = d.historicoData || [];
  const funil = d.funilImplantacao || [];
  const pendencias = d.implantacoesPendentes || [];
  const crescimento = d.crescimentoGeral || {};

  const totalCadastradas = funil.length > 0 ? funil[0].value : 0;
  const totalGoLive = funil.length > 0 ? funil[funil.length - 1].value : 0;
  
  const taxaConclusao = totalCadastradas > 0 
    ? Math.round((totalGoLive / totalCadastradas) * 100 * 10) / 10 
    : 0;

  // Metas do trimestre recalibradas com histórico real
  const ultimoMesAlunos = historico.length > 0 ? historico[historico.length - 1].alunos : 0;
  const METAS_TRIMESTRE = [
    { label: 'Escolas Go-Live', meta: totalCadastradas || 10, atual: totalGoLive, unidade: 'escolas' },
    { label: 'Alunos Conectados', meta: Math.max(ultimoMesAlunos + 500, 2000), atual: ultimoMesAlunos, unidade: 'alunos' },
    { label: 'Retenção', meta: 100, atual: crescimento.retencao || 0, unidade: '%' },
    { label: 'Novos Contratos Anual', meta: 50, atual: crescimento.aquisicaoAnual || 0, unidade: 'novos' },
  ];

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ HERO ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 250, height: 250, borderRadius: '50%', background: 'rgba(251,191,36,.06)' }} />
        <div style={{ position: 'absolute', bottom: -40, left: '30%', width: 200, height: 200, borderRadius: '50%', background: 'rgba(16,185,129,.05)' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Rocket size={18} style={{ color: '#fbbf24' }} />
            <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>
              Expansão & Onboarding
            </span>
          </div>
          <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: '0 0 4px' }}>
            Funil de Implantação
          </h1>
          <p style={{ color: 'rgba(255,255,255,.4)', fontSize: '.82rem', marginBottom: 24 }}>
            Monitoramento em tempo real do setup de escolas
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
            {[
              { icon: Building2, value: totalCadastradas, label: 'Escolas na Base', color: '#a78bfa' },
              { icon: Flag, value: `${taxaConclusao}%`, label: 'Taxa de Go-Live', color: '#10b981' },
              { icon: AlertTriangle, value: pendencias.length, label: 'Implantações Pendentes', color: '#fbbf24' },
              { icon: Play, value: totalGoLive, label: 'Operação Completa', color: '#38bdf8' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${s.color}20`, color: s.color, backdropFilter: 'blur(10px)',
                }}>
                  <s.icon size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '.78rem', color: 'rgba(255,255,255,.7)', fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ METAS Q2 ═══ */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Target size={18} style={{ color: '#f59e0b' }} />
          Metas Operacionais
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {METAS_TRIMESTRE.map(m => <MetaCard key={m.label} {...m} />)}
        </div>
      </div>

      {/* ═══ FUNNEL + GROWTH ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>

        {/* Funnel */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <h3 style={{ fontSize: '.95rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Server size={18} style={{ color: '#6366f1' }} />
            Etapas de Onboarding (Real)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {funil.map((step: any, i: number) => {
              const baseValue = funil[0]?.value || 1;
              const pct = Math.round((step.value / baseValue) * 100);
              return (
                <div key={step.name} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 24, textAlign: 'right', fontSize: '.75rem', color: 'var(--text-muted, #9ca3af)', fontWeight: 600 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: '.8rem', fontWeight: 600 }}>{step.name}</span>
                      <span style={{ fontSize: '.8rem', fontWeight: 800, color: step.fill }}>{step.value}</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 4, background: 'var(--border-color, #e5e7eb)' }}>
                      <div style={{
                        height: '100%', borderRadius: 4, background: step.fill,
                        width: `${pct}%`, transition: 'width .6s ease',
                      }} />
                    </div>
                  </div>
                  <span style={{ width: 36, fontSize: '.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>{pct}%</span>
                </div>
              );
            })}
          </div>
          <div style={{
            marginTop: 16, padding: '10px 14px', borderRadius: 10,
            background: 'var(--bg-main, #f9fafb)', fontSize: '.78rem', color: 'var(--text-muted, #6b7280)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Zap size={14} style={{ color: '#10b981' }} />
            <span>Escolas com operações diárias orgânicas (com acessos na base recentemente).</span>
          </div>
        </div>

        {/* Growth Chart */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
        }}>
          <h3 style={{ fontSize: '.95rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp size={18} style={{ color: '#10b981' }} />
            Evolução Mensal (Cadastros)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={historico}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '.82rem' }} />
              <Bar dataKey="escolas" name="Escolas" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ═══ PENDÊNCIAS DE SETUP ═══ */}
      <div style={{
        background: 'var(--bg-card, white)', borderRadius: 16,
        border: '1px solid var(--border-color, #e5e7eb)', padding: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: '.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <ShieldAlert size={18} style={{ color: '#ef4444' }} />
            Implantações Pendentes ({pendencias.length} escolas travadas)
          </h3>
          <div style={{
            padding: '4px 12px', borderRadius: 8, fontSize: '.75rem', fontWeight: 700,
            background: '#ef444415', color: '#ef4444',
          }}>
            Foco Operacional
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pendencias.length > 0 ? (
            pendencias.sort((a: any, b: any) => b.diasAberto - a.diasAberto).map((deal: any, i: number) => (
              <PendenciaRow key={i} deal={deal} />
            ))
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--text-muted, #9ca3af)', padding: '30px 0' }}>
              <CheckCircle2 size={32} style={{ margin: '0 auto 8px', color: '#10b981' }} />
              <p>Nenhuma implantação pendente. Todas as escolas estão em operação (Go-Live).</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        .animate-fadeIn { animation: fadeIn .4s ease; }
        @media (max-width: 1024px) {
          div[style*="repeat(4, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
          div[style*="1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
